import React, { useEffect, useState } from "react";
import {
  useReactTable,
  getCoreRowModel,
  getFilteredRowModel,
  flexRender,
} from "@tanstack/react-table";
import { FaSearch, FaPlus, FaMinus } from "react-icons/fa";
import { useDispatch, useSelector } from "react-redux";
import {
  setDrillDownDistrictCode,
  setDrillDownTalukCode,
  setDrillDownVillageCode,
  setTableChartId,
  setTableHeading,
  setTalukCode,
} from "../../applicationStatus/store";
import { useParams } from "react-router-dom";
import NoteComponent from "./Notes";
import DynamicLoader from "../../Form/FormFields/DynamicLoader";
import Level2Table from "./Level2Table";
import {
  DynamicData,
  TableProps,
  removeZeroColumns,
  convertColumns,
  formatDateTime,
  formatNumberWithCommas,
  getStatusColor,
} from "./tableUtils";

const DynamicTable: React.FC<TableProps> = ({
  columns,
  fetchData,
  type,
  applicationStatusData,
  has_drilldown,
  drill_down_chart_id,
  drill_down_level3_chart_id,
  fetchPopupData,
  id,
  title,
}) => {
  const location = useParams();
  const dispatch = useDispatch();

  // ── Level-1 own state ──
  const [data, setData] = useState<any[]>([]);
  const [dynamicHeight, setDynamicTableheight] = useState<string | number>(
    "1200px",
  );
  const [expandedRow, setExpandedRow] = useState<number | null>(null);
  const [subData, setSubData] = useState<{ [key: number]: any[] }>({});
  const [globalFilter, setGlobalFilter] = useState("");

  // ── Redux selectors ──
  const selectorData = useSelector(
    (state: { dynamicData: DynamicData }) => state?.dynamicData,
  );

  const responseDataEntry = (selectorData as any)?.[id as any];
  const effectiveDrillDownId =
    responseDataEntry?.drill_down_chart_id || drill_down_chart_id;

  // Effective level-3 chart id
  const level3ChartId = drill_down_level3_chart_id || effectiveDrillDownId;

  const notes = id ? responseDataEntry?.notes : null;
  const noteData = id ? responseDataEntry?.remarks : null;
  const isMainLoading = selectorData.loaders[`report/chart/${id}`];

  const isLevel2Loading = effectiveDrillDownId
    ? selectorData.loaders[`report/chart/${effectiveDrillDownId}`]
    : false;

  // ── Reset all drilldown state when chart id changes ──
  useEffect(() => {
    setData([]);
    setSubData({});
    setExpandedRow(null);
    setGlobalFilter("");
    dispatch(setDrillDownDistrictCode(""));
    dispatch(setDrillDownTalukCode(""));
    dispatch(setDrillDownVillageCode(""));
  }, [id]);

  // ── Load main table data from Redux ──
  useEffect(() => {
    const entry = (selectorData as any)?.[id as any];
    if (!id || !entry) {
      setData([]);
      return;
    }

    let nextData: any[] = [];
    if (Array.isArray(entry)) {
      nextData = entry;
    } else if (Array.isArray(entry?.data?.rows)) {
      nextData = entry.data.rows;
    } else if (Array.isArray(entry?.data)) {
      nextData = entry.data;
    } else if (Array.isArray(entry?.rows)) {
      nextData = entry.rows;
    }
    setData(nextData);
  }, [id, selectorData]);

  // ── Dynamic height ──
  useEffect(() => {
    setDynamicTableheight(location.id !== "6" ? "1200px" : "500px");
  }, [location]);

  // ── Sync level-2 sub data from Redux ──
  useEffect(() => {
    if (!expandedRow && expandedRow !== 0) return;
    if (!effectiveDrillDownId) return;

    const entry = (selectorData as any)[effectiveDrillDownId as any];
    if (!entry) return;

    let subRows: any[] = [];
    if (Array.isArray(entry)) {
      subRows = entry;
    } else if (Array.isArray(entry?.data?.rows)) {
      subRows = entry.data.rows;
    } else if (Array.isArray(entry?.data)) {
      subRows = entry.data;
    } else if (Array.isArray(entry?.rows)) {
      subRows = entry.rows;
    }

    setSubData((prev) => ({
      ...prev,
      [expandedRow]: subRows,
    }));
  }, [selectorData, effectiveDrillDownId, expandedRow]);

  // ── Level-1 expand handler ──
  const handleExpand = async (rowIndex: number, row: any) => {
    setSubData([]);
    if (expandedRow === rowIndex) {
      setExpandedRow(null);
      await dispatch(setDrillDownDistrictCode(""));
      await dispatch(setDrillDownTalukCode(""));
      await dispatch(setDrillDownVillageCode(""));
    } else {
      setExpandedRow(rowIndex);
      await dispatch(setDrillDownTalukCode(""));
      await dispatch(setDrillDownVillageCode(""));
      // Clear leftover popup-only filters (taluk code, column) from any
      // earlier popup click, so they don't leak into this plain drilldown fetch.
      await dispatch(setTalukCode(""));
      await dispatch(setTableChartId(""));
      await dispatch(setTableHeading({ id, accessorKey: "" } as any));
      if (row?.district_code) {
        await dispatch(setDrillDownDistrictCode(row.district_code));
      }
      if (applicationStatusData && effectiveDrillDownId !== undefined) {
        await applicationStatusData(Number(effectiveDrillDownId));
      }
    }
  };

  // ── Build tanstack columns ──
  const formattedColumns = React.useMemo(() => {
    const filtered = removeZeroColumns(data, columns);
    return convertColumns(filtered);
  }, [columns, data]);

  const table = useReactTable({
    data,
    columns: formattedColumns,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    state: { globalFilter },
    onGlobalFilterChange: setGlobalFilter,
  });

  return (
    <div className="overflow-x-auto">
      {/* Search Box */}
      <div className="p-5 rounded-t-2xl bg-white border-b border-gray-100 shadow-sm">
        <div className="flex flex-col col-span-3 md:flex-row justify-between items-center gap-4">
          <div className="flex w-full flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div className="flex items-center gap-1 min-w-0">
              <h2 className="text-xl font-bold text-slate-800 tracking-tight whitespace-nowrap">
                {title}
              </h2>
              {notes && <NoteComponent notes={noteData} modalTitle={title} />}
            </div>

            <div className="relative w-full md:w-72">
              <FaSearch className="absolute left-3 top-1/2 -translate-y-1/3 text-slate-400 transition-colors" />
              <input
                type="text"
                placeholder="Search reports..."
                value={globalFilter}
                onChange={(e) => setGlobalFilter(e.target.value)}
                className="w-full pl-10 pr-4 py-2 bg-slate-50 border border-slate-200 rounded-xl text-sm text-slate-700 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all shadow-sm"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Table */}
      <div
        style={{ height: dynamicHeight }}
        className="overflow-y-auto scrollbar-thin-black bg-white rounded-b-2xl shadow-lg border border-white"
      >
        <table className="w-full text-left border-separate border-spacing-0">
          <thead className="text-center sticky top-0">
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <th
                    key={header.id}
                    colSpan={header.colSpan}
                    className="sticky top-0 z-10 px-6 py-4 bg-[#1e1e2d] text-slate-100 text-[13px] font-bold uppercase tracking-wider border border-white text-nowrap bg-clip-padding"
                  >
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext(),
                        )}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {isMainLoading ? (
              <tr>
                <td colSpan={columns.length + 2} className="p-10 text-center">
                  <DynamicLoader />
                </td>
              </tr>
            ) : table.getRowModel().rows.length > 0 ? (
              table.getRowModel().rows.map((row, rowIndex) => {
                const canDrillIn =
                  has_drilldown && Number((row.original as any)?.expand) === 1;

                return (
                  <React.Fragment key={rowIndex}>
                    {/* Level-1 main row */}
                    <tr className="hover:bg-gray-50">
                      {row.getVisibleCells().map((cell, cellIndex) => {
                        const accessorKey = (cell.column.columnDef as any)
                          .accessorKey;
                        const rawValue = cell.getValue();

                        // ── expand column ──
                        if (cell.column.id === "expand") {
                          return (
                            <td
                              key={cellIndex}
                              className="px-6 py-4 text-center border-b border-slate-50"
                            >
                              {canDrillIn ? (
                                <button
                                  onClick={async () => {
                                    await dispatch(setTableChartId(""));
                                    handleExpand(rowIndex, row.original);
                                  }}
                                  className={`p-1.5 rounded-lg transition-all duration-200 ${
                                    expandedRow === rowIndex
                                      ? "bg-red-50 text-red-600 hover:bg-red-100"
                                      : "bg-blue-50 text-blue-600 hover:bg-blue-100"
                                  } shadow-sm border border-transparent hover:border-current/20`}
                                >
                                  {expandedRow === rowIndex ? (
                                    <FaMinus size={12} title="Collapse" />
                                  ) : (
                                    <FaPlus size={12} title="Expand" />
                                  )}
                                </button>
                              ) : null}
                            </td>
                          );
                        }

                        // ── regular data column ──
                        let displayNode: React.ReactNode = null;
                        if (cell.column.id === "created_at") {
                          displayNode = formatDateTime(rawValue as string);
                        } else if (cell.column.id === "status") {
                          displayNode = (
                            <span
                              className={`px-3 py-1 rounded-full text-sm font-semibold ${getStatusColor(
                                rawValue as string,
                              )}`}
                            >
                              {rawValue as string}
                            </span>
                          );
                        } else if (
                          typeof rawValue === "number" ||
                          rawValue == null ||
                          (typeof rawValue === "string" &&
                            /^-?\d+(?:\.\d+)?$/.test(
                              (rawValue as string).replace(/,/g, ""),
                            ))
                        ) {
                          displayNode = formatNumberWithCommas(rawValue);
                        } else {
                          displayNode = flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext(),
                          );
                        }

                        return (
                          <td
                            key={cellIndex}
                            className="px-6 py-4 border-b border-slate-50 text-slate-600 text-[13px] font-medium text-center transition-colors group-hover:bg-blue-50/30"
                            onClick={async () => {
                              await dispatch(
                                setTableHeading({ id, accessorKey } as any),
                              );
                              if (
                                !["district_name", "expand"].includes(
                                  cell.column.id,
                                )
                              ) {
                                await dispatch(setDrillDownDistrictCode(""));
                                // Clear any taluk code left over from a
                                // previous taluk-level popup click, or it
                                // leaks into this district-level popup query.
                                await dispatch(setTalukCode(""));
                                if ((row.original as any)?.district_code) {
                                  await dispatch(
                                    setTableChartId(
                                      (row.original as any).district_code,
                                    ),
                                  );
                                }
                                if (fetchPopupData)
                                  fetchPopupData(row?.original, "district");
                              } else {
                                await dispatch(setTableChartId(""));
                              }
                            }}
                          >
                            {displayNode}
                          </td>
                        );
                      })}
                    </tr>

                    {/* Level-2 drilldown (taluk) */}
                    {expandedRow === rowIndex && (
                      <tr>
                        <td
                          colSpan={columns.length + 2}
                          className="p-6 bg-slate-50/80 border-b border-slate-200"
                        >
                          <div className="bg-white rounded-xl shadow-md border border-slate-200 overflow-hidden">
                            {isLevel2Loading ? (
                              <div className="p-10 text-center">
                                <DynamicLoader />
                              </div>
                            ) : (selectorData as any)?.[
                                effectiveDrillDownId as any
                              ] ? (
                              <Level2Table
                                selectorData={selectorData}
                                level2ChartId={effectiveDrillDownId}
                                level3ChartId={level3ChartId}
                                subData={subData[expandedRow] ?? []}
                                dispatch={dispatch}
                                id={id}
                                applicationStatusData={applicationStatusData}
                                fetchPopupData={fetchPopupData}
                                globalFilter={globalFilter}
                              />
                            ) : (
                              <div className="p-10 text-center text-gray-500 font-medium">
                                No data available
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })
            ) : (
              <tr>
                <td
                  colSpan={columns.length + 2}
                  className="p-3 text-center text-gray-500"
                >
                  No data available
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default DynamicTable;
